import Link from 'next/link'
import {
  ArrowRight,
  Database,
  Globe,
  KeyRound,
  Mail,
  MessageCircle,
  Shield,
  UploadCloud,
  UsersRound,
} from 'lucide-react'
import { PageHeader } from '@/components/layout/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useAsyncData } from '@/src/hooks/useAsyncData'
import { getHelmSessionUser } from '@/src/lib/auth'
import { LoadingState } from '@/src/components/LoadingState'
import { ErrorState } from '@/src/components/ErrorState'
import { NotificationPreferencesCard } from '@/src/components/NotificationPreferencesCard'
import { SignInEmailCard } from '@/src/components/SignInEmailCard'
import { API_BASE_URL } from '@/lib/env'
import { apiClient } from '@/lib/api/client'

const settingsGroups = [
  {
    title: 'Runtime',
    items: [
      { label: 'Cluster API', value: API_BASE_URL, icon: Globe },
      { label: 'Authentication', value: 'Better Auth sessions', icon: Shield },
      { label: 'Database', value: 'Owned Postgres', icon: Database },
    ],
  },
  {
    title: 'Operations',
    items: [
      { label: 'File Uploads', value: 'Managed object storage', icon: UploadCloud },
      { label: 'Email Updates', value: 'Planned', icon: Mail },
    ],
  },
]

export function SettingsPage() {
  const { data, error, loading, reload } = useAsyncData(async () => {
    const [user, profile] = await Promise.all([
      getHelmSessionUser(),
      apiClient.getProfile().catch(() => null),
    ])
    return { user, isAdmin: !!profile?.is_admin || profile?.role === 'admin' }
  }, [])
  const user = data?.user

  if (loading) return <LoadingState label="Loading settings..." />
  if (error) return <ErrorState error={error} onRetry={reload} />

  return (
    <div className="space-y-6">
      <PageHeader
        title="Settings"
        description="Runtime and operations configuration"
        breadcrumbs={[{ label: 'Settings' }]}
      />

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" />
              Current Session
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 text-sm md:grid-cols-2">
            <div>
              <div className="text-muted-foreground">User</div>
              <div className="font-medium">{user?.email || user?.name || user?.id}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Status</div>
              <Badge variant="secondary">Authenticated</Badge>
            </div>
          </CardContent>
        </Card>

        <SignInEmailCard />

        <NotificationPreferencesCard />

        <div className="grid gap-4 lg:grid-cols-2">
          {data?.isAdmin && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="size-5" />
                  WhatsApp
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <p className="text-sm text-muted-foreground">
                  Pair the business phone, connect customer groups and review captured
                  conversations.
                </p>
                <Link href="/settings/whatsapp">
                  <Button variant="outline" size="sm">
                    Open WhatsApp
                    <ArrowRight data-icon="inline-end" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UsersRound className="h-5 w-5" />
                Users
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Manage Helm staff roles and pending invitations.
              </p>
              <Link href="/settings/users">
                <Button variant="outline" size="sm">
                  Open Users
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          {settingsGroups.map((group) => (
            <Card key={group.title}>
              <CardHeader>
                <CardTitle>{group.title}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {group.items.map((item) => (
                  <div key={item.label} className="flex items-start gap-3">
                    <item.icon className="mt-0.5 h-4 w-4 text-muted-foreground" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{item.label}</div>
                      <div className="break-words text-sm text-muted-foreground">{item.value}</div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  )
}
